import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const ExamContext = createContext(null);

export const SECTION_CONFIG = {
  VR: { name: 'Verbal Reasoning', questions: 44, timeMinutes: 22, instructionTime: 90 },
  DM: { name: 'Decision Making', questions: 35, timeMinutes: 37, instructionTime: 90 },
  QR: { name: 'Quantitative Reasoning', questions: 36, timeMinutes: 26, instructionTime: 120 },
  SJT: { name: 'Situational Judgement', questions: 69, timeMinutes: 26, instructionTime: 90 }
};

export const TIMING_PRESETS = {
  standard: { name: 'Standard', multiplier: 1.0 },
  UCATSEN: { name: 'UCATSEN (25% extra)', multiplier: 1.25 },
  UCATSEN50: { name: 'UCATSEN50 (50% extra)', multiplier: 1.5 }
};

export const ExamProvider = ({ children }) => {
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [sessionType, setSessionType] = useState(null); // 'practice', 'mock', 'trainer'
  const [currentSection, setCurrentSection] = useState(null);
  const [sections, setSections] = useState([]);
  
  // Question state
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  
  // Question timing - track time spent on each question
  const [questionTimes, setQuestionTimes] = useState({});
  const questionTimesRef = useRef({});
  const questionStartTimeRef = useRef(Date.now());
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTimerWarning, setIsTimerWarning] = useState(false);
  const timerRef = useRef(null);
  
  // UI state
  const [showCalculator, setShowCalculator] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false);
  const [isExamMode, setIsExamMode] = useState(false);
  const [examPhase, setExamPhase] = useState('intro'); // 'intro', 'instructions', 'exam', 'review', 'results'
  
  // Timing preset
  const [timingPreset, setTimingPreset] = useState('standard');

  // State to track if this is an untimed session
  const [isUntimedSession, setIsUntimedSession] = useState(false);
  const clockStartedRef = useRef(false);

  // Timer effect - don't tick for untimed practice
  useEffect(() => {
    if (isUntimedSession || timeRemaining >= 32400) {
      // Don't tick for untimed sessions
      clearInterval(timerRef.current);
      return;
    }
    
    if (timeRemaining > 0 && !isPaused && examPhase === 'exam') {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          // Warning at 5 minutes (300 seconds)
          setIsTimerWarning(newTime <= 300 && newTime > 0);
          return newTime;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [timeRemaining, isPaused, examPhase, isUntimedSession]);

  // Time expired handler
  useEffect(() => {
    const isUntimed = timeRemaining >= 32400 || (timeRemaining === 0 && isPaused);
    if (!clockStartedRef.current) return;
    if (timeRemaining === 0 && examPhase === 'exam' && !isUntimed) {
      clockStartedRef.current = false;
      handleTimeExpired();
    }
  }, [timeRemaining, examPhase, isPaused]);

  // Keyboard shortcuts live in ExamLayout (practice, mock, and race).
  // Do not register Alt+C / Alt+V here — a second listener double-toggles and
  // makes the calculator/navigator shortcuts appear broken.

  const startSession = useCallback((config) => {
    setSessionId(config.sessionId);
    setSessionType(config.sessionType);
    setSections(config.sections || []);
    setTimingPreset(config.timingPreset || 'standard');
    setIsExamMode(true);
    setExamPhase('intro');
    setAnswers({});
    setFlaggedQuestions(new Set());
    setQuestionTimes({});
    questionTimesRef.current = {};
    questionStartTimeRef.current = Date.now();
    setTimeExpiredFlag(false);
    setIsPaused(false);
    clockStartedRef.current = false;
    setTimeRemaining(null);
  }, []);

  const startSection = useCallback((section, sectionQuestions, customTimeMinutes = null) => {
    setCurrentSection(section);
    setQuestions(sectionQuestions);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setShowCalculator(false);
    setShowNavigator(false);
    setQuestionTimes({});
    questionTimesRef.current = {};
    questionStartTimeRef.current = Date.now();
    
    // Use custom time limit if provided, otherwise use section default
    let baseTimeSeconds;
    let isUntimed = false;
    
    if (customTimeMinutes !== null && customTimeMinutes > 0) {
      baseTimeSeconds = customTimeMinutes * 60;
    } else if (customTimeMinutes === 0 || customTimeMinutes === false) {
      // Untimed practice - set a very large time (10 hours)
      baseTimeSeconds = 36000;
      isUntimed = true;
    } else {
      baseTimeSeconds = SECTION_CONFIG[section].timeMinutes * 60;
    }
    
    // Set untimed flag BEFORE setting time
    setIsUntimedSession(isUntimed);
    
    // Apply timing preset multiplier
    const multiplier = TIMING_PRESETS[timingPreset]?.multiplier || 1;
    const next = Math.floor(baseTimeSeconds * multiplier);
    clockStartedRef.current = next > 0 && !isUntimed;
    setTimeRemaining(next);
    setTimeExpiredFlag(false);

    setExamPhase('exam');
    setIsPaused(false);
    setIsTimerWarning(false);
  }, [timingPreset]);

  // Record time spent on current question (syncs to both state and ref)
  const recordQuestionTime = useCallback(() => {
    if (questions.length === 0) return;
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;
    
    const timeSpent = Math.round((Date.now() - questionStartTimeRef.current) / 1000);
    const newTime = (questionTimesRef.current[currentQuestion.id] || 0) + timeSpent;
    questionTimesRef.current[currentQuestion.id] = newTime;
    setQuestionTimes(prev => ({
      ...prev,
      [currentQuestion.id]: newTime
    }));
    questionStartTimeRef.current = Date.now();
  }, [questions, currentQuestionIndex]);

  const goToNextQuestion = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      recordQuestionTime();
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [currentQuestionIndex, questions.length, recordQuestionTime]);

  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      recordQuestionTime();
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [currentQuestionIndex, recordQuestionTime]);

  const goToQuestion = useCallback((index) => {
    if (index >= 0 && index < questions.length) {
      recordQuestionTime();
      setCurrentQuestionIndex(index);
      setShowNavigator(false);
    }
  }, [questions.length, recordQuestionTime]);

  const selectAnswer = useCallback((questionId, optionIds) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionIds
    }));
  }, []);

  const toggleFlag = useCallback((questionIndex) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionIndex)) {
        newSet.delete(questionIndex);
      } else {
        newSet.add(questionIndex);
      }
      return newSet;
    });
  }, []);

  const handleTimeExpired = useCallback(() => {
    setIsPaused(true);
    // Set a flag that tells ExamPage to auto-submit
    setTimeExpiredFlag(true);
  }, []);
  
  // State to track if time expired (used by ExamPage to auto-submit)
  const [timeExpiredFlag, setTimeExpiredFlag] = useState(false);

  const getQuestionStatus = useCallback((index) => {
    const question = questions[index];
    if (!question) return 'unseen';
    
    const answer = answers[question.id];
    const isFlagged = flaggedQuestions.has(index);
    
    // Check if it's a Yes/No question
    const isYesNoQuestion = question.question_type === 'yes_no_statements' || 
      (question.yes_no_statements?.length > 0) || (question.statements?.length > 0);
    const statements = question.yes_no_statements || question.statements || [];
    
    // Determine completion status based on question type
    let isComplete = false;
    let isPartiallyAnswered = false;
    
    if (isYesNoQuestion && statements.length > 0) {
      // For Yes/No questions: check if ALL statements have been answered
      if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
        const answeredCount = Object.keys(answer).filter(key => answer[key] !== null && answer[key] !== undefined).length;
        isComplete = answeredCount >= statements.length;
        isPartiallyAnswered = answeredCount > 0 && answeredCount < statements.length;
      }
    } else if (question.question_type === 'most_least_appropriate' || question.question_type === 'sjt_drag_drop') {
      // SJT drag-drop: both most AND least must be selected
      if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
        const hasMost = !!answer.most;
        const hasLeast = !!answer.least;
        isComplete = hasMost && hasLeast;
        isPartiallyAnswered = (hasMost || hasLeast) && !isComplete;
      }
    } else {
      // For MCQ questions: check if at least one option is selected
      if (Array.isArray(answer)) {
        isComplete = answer.length > 0;
      } else if (typeof answer === 'object' && answer !== null) {
        isComplete = Object.keys(answer).length > 0;
      }
    }
    
    if (isComplete) return isFlagged ? 'complete-flagged' : 'complete';
    if (isPartiallyAnswered || index <= currentQuestionIndex) return isFlagged ? 'incomplete-flagged' : 'incomplete';
    return 'unseen';
  }, [questions, answers, flaggedQuestions, currentQuestionIndex]);

  const getSessionSummary = useCallback(() => {
    const totalQuestions = questions.length;
    
    // Helper to check if a question is fully answered
    const isQuestionComplete = (question) => {
      const answer = answers[question.id];
      const isYesNoQuestion = question.question_type === 'yes_no_statements' || 
        (question.yes_no_statements?.length > 0) || (question.statements?.length > 0);
      const statements = question.yes_no_statements || question.statements || [];
      
      if (isYesNoQuestion && statements.length > 0) {
        if (typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
          const answeredCount = Object.keys(answer).filter(key => answer[key] !== null && answer[key] !== undefined).length;
          return answeredCount >= statements.length;
        }
        return false;
      } else {
        if (Array.isArray(answer)) {
          return answer.length > 0;
        } else if (typeof answer === 'object' && answer !== null) {
          return Object.keys(answer).length > 0;
        }
        return false;
      }
    };
    
    const answeredCount = questions.filter(q => isQuestionComplete(q)).length;
    const flaggedCount = flaggedQuestions.size;
    const unansweredCount = totalQuestions - answeredCount;

    return {
      totalQuestions,
      answeredCount,
      flaggedCount,
      unansweredCount,
      timeRemaining,
      section: currentSection
    };
  }, [questions, answers, flaggedQuestions, timeRemaining, currentSection]);

  const endSection = useCallback(() => {
    setExamPhase('review');
    setIsPaused(true);
  }, []);

  const endExam = useCallback(() => {
    setIsExamMode(false);
    setExamPhase('results');
    clearInterval(timerRef.current);
  }, []);

  const resetExam = useCallback(() => {
    setSessionId(null);
    setSessionType(null);
    setCurrentSection(null);
    setSections([]);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setTimeRemaining(0);
    setIsPaused(false);
    setIsTimerWarning(false);
    setShowCalculator(false);
    setShowNavigator(false);
    setIsExamMode(false);
    setExamPhase('intro');
    clearInterval(timerRef.current);
  }, []);

  // Restore saved session state (for resuming)
  const restoreSessionState = useCallback((savedState) => {
    if (savedState.answers && typeof savedState.answers === 'object') {
      setAnswers(savedState.answers);
    }
    if (savedState.flaggedQuestions) {
      setFlaggedQuestions(new Set(savedState.flaggedQuestions));
    }
    if (typeof savedState.currentIndex === 'number') {
      setCurrentQuestionIndex(savedState.currentIndex);
    }
    if (typeof savedState.timeRemaining === 'number' && savedState.timeRemaining > 0) {
      clockStartedRef.current = true;
      setTimeRemaining(savedState.timeRemaining);
    }
  }, []);

  const value = {
    // Session state
    sessionId,
    sessionType,
    currentSection,
    sections,
    
    // Question state
    questions,
    currentQuestionIndex,
    currentQuestion: questions[currentQuestionIndex],
    answers,
    flaggedQuestions,
    questionTimes,
    questionTimesRef,
    
    // Timer state
    timeRemaining,
    isPaused,
    isTimerWarning,
    timingPreset,
    isUntimedSession,
    timeExpiredFlag,
    setTimeExpiredFlag,
    
    // UI state
    showCalculator,
    showNavigator,
    isExamMode,
    examPhase,
    
    // Actions
    startSession,
    startSection,
    goToNextQuestion,
    goToPreviousQuestion,
    goToQuestion,
    selectAnswer,
    toggleFlag,
    getQuestionStatus,
    getSessionSummary,
    endSection,
    endExam,
    resetExam,
    restoreSessionState,
    recordQuestionTime,
    setShowCalculator,
    setShowNavigator,
    setExamPhase,
    setIsPaused,
    setTimingPreset,
    
    // Config
    SECTION_CONFIG,
    TIMING_PRESETS
  };

  return (
    <ExamContext.Provider value={value}>
      {children}
    </ExamContext.Provider>
  );
};

export const useExam = () => {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error('useExam must be used within an ExamProvider');
  }
  return context;
};
